import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Fade,
    IconButton,
    InputAdornment,
    LinearProgress,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ClearAllRoundedIcon from "@mui/icons-material/ClearAllRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";

import Swal from "sweetalert2";
import { tokens } from "../../theme";
import { djangoApi } from "../../utils/axios/axios.utils";

function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "-";
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function isImageFile(file) {
    return file?.type?.startsWith("image/");
}

function buildPreviewItems(files) {
    return files.map((file, index) => ({
        id: `${file.name}-${file.lastModified}-${index}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        previewUrl: URL.createObjectURL(file),
        uploadStatus: "ready",
    }));
}

export default function UploadImgsPage() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const inputRef = useRef(null);

    const [selectedItems, setSelectedItems] = useState([]);
    const [subfolder, setSubfolder] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgressText, setUploadProgressText] = useState("");
    const [result, setResult] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [previewImage, setPreviewImage] = useState(null);

    const totalSize = useMemo(() => {
        return selectedItems.reduce((acc, item) => acc + (item?.size || 0), 0);
    }, [selectedItems]);

    const filteredSelectedItems = useMemo(() => {
        const term = String(searchTerm || "").trim().toLowerCase();
        if (!term) return selectedItems;
        return selectedItems.filter((item) =>
            String(item.name || "").toLowerCase().includes(term)
        );
    }, [selectedItems, searchTerm]);

    const filteredUploadedItems = useMemo(() => {
        const term = String(searchTerm || "").trim().toLowerCase();
        const uploaded = result?.uploaded || [];
        if (!term) return uploaded;
        return uploaded.filter((item) =>
            String(item.filename || "").toLowerCase().includes(term)
        );
    }, [result, searchTerm]);

    useEffect(() => {
        return () => {
            selectedItems.forEach((item) => {
                if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            });
        };
    }, [selectedItems]);

    const pageBg =
        theme.palette.mode === "dark"
            ? "linear-gradient(180deg, #121212 0%, #0c0c0c 100%)"
            : "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)";

    const glassBg =
        theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.04)"
            : "rgba(255,255,255,0.82)";

    const glassBorder =
        theme.palette.mode === "dark"
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(15,23,42,0.08)";

    const softShadow =
        theme.palette.mode === "dark"
            ? "0 10px 30px rgba(0,0,0,0.28)"
            : "0 12px 32px rgba(15,23,42,0.08)";

    const mutedText =
        theme.palette.mode === "dark"
            ? alpha("#ffffff", 0.72)
            : alpha("#0f172a", 0.72);

    const neutralChipBg =
        theme.palette.mode === "dark"
            ? alpha("#ffffff", 0.06)
            : alpha("#0f172a", 0.05);

    const handleAddFiles = (rawFiles) => {
        const files = Array.from(rawFiles || []);
        if (!files.length) return;

        const invalidFiles = files.filter((file) => !isImageFile(file));
        const validFiles = files.filter((file) => isImageFile(file));

        if (invalidFiles.length) {
            Swal.fire({
                icon: "warning",
                title: "Arquivos ignorados",
                text: `${invalidFiles.length} arquivo(s) não são imagens válidas.`,
                confirmButtonText: "Ok",
            });
        }

        if (!validFiles.length) return;

        const newItems = buildPreviewItems(validFiles);

        setSelectedItems((prev) => {
            const existingKeys = new Set(
                prev.map((item) => `${item.name}-${item.size}-${item.file?.lastModified}`)
            );

            const deduped = newItems.filter(
                (item) => !existingKeys.has(`${item.name}-${item.size}-${item.file?.lastModified}`)
            );

            return [...prev, ...deduped];
        });
    };

    const handleFilesSelected = (event) => {
        handleAddFiles(event.target.files);
        event.target.value = "";
    };

    const handleRemoveItem = (id) => {
        setSelectedItems((prev) => {
            const found = prev.find((item) => item.id === id);
            if (found?.previewUrl) URL.revokeObjectURL(found.previewUrl);
            return prev.filter((item) => item.id !== id);
        });
    };

    const handleClearAll = async () => {
        if (!selectedItems.length) return;

        const res = await Swal.fire({
            icon: "question",
            title: "Limpar seleção?",
            text: "Todas as imagens selecionadas serão removidas da grade.",
            showCancelButton: true,
            confirmButtonText: "Limpar",
            cancelButtonText: "Cancelar",
        });

        if (!res.isConfirmed) return;

        selectedItems.forEach((item) => {
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });

        setSelectedItems([]);
    };

    const handleUpload = async () => {
        if (!selectedItems.length) {
            Swal.fire({
                icon: "warning",
                title: "Nenhuma imagem selecionada",
                text: "Selecione ao menos uma imagem para enviar.",
                confirmButtonText: "Ok",
            });
            return;
        }

        const confirm = await Swal.fire({
            icon: "question",
            title: "Enviar imagens?",
            html: `
        <div style="text-align:left">
          <p><b>Quantidade:</b> ${selectedItems.length}</p>
          <p><b>Tamanho total:</b> ${formatBytes(totalSize)}</p>
          <p><b>Subpasta:</b> ${subfolder?.trim() || "productapp"}</p>
        </div>
      `,
            showCancelButton: true,
            confirmButtonText: "Enviar agora",
            cancelButtonText: "Cancelar",
        });

        if (!confirm.isConfirmed) return;

        try {
            setIsUploading(true);
            setUploadProgressText("Preparando imagens...");
            setResult(null);

            const formData = new FormData();

            selectedItems.forEach((item) => {
                formData.append("images", item.file);
            });

            if (String(subfolder || "").trim()) {
                formData.append("subfolder", String(subfolder).trim());
            }

            Swal.fire({
                title: "Processando upload",
                html: `
          <div style="display:flex;flex-direction:column;align-items:center;gap:10px;">
            <div class="swal2-loader" style="display:block;"></div>
            <div>As imagens estão sendo tratadas e enviadas para o storage.</div>
          </div>
        `,
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
            });

            setUploadProgressText(`Enviando ${selectedItems.length} imagem(ns)...`);

            const response = await djangoApi.post(
                "products/firebase/upload-imgs/",
                formData,
                {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const data = response?.data || {};
            setResult(data);

            const uploadedCount = Number(data?.uploaded_count || 0);
            const errorCount = Number(data?.error_count || 0);

            setUploadProgressText("Upload concluído");

            await Swal.fire({
                icon: errorCount > 0 ? "warning" : "success",
                title: errorCount > 0 ? "Concluído com alertas" : "Upload concluído",
                html: `
          <div style="text-align:left">
            <p><b>Enviadas:</b> ${uploadedCount}</p>
            <p><b>Erros:</b> ${errorCount}</p>
          </div>
        `,
                confirmButtonText: "Ok",
            });

            if (uploadedCount > 0) {
                selectedItems.forEach((item) => {
                    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
                });
                setSelectedItems([]);
            }
        } catch (error) {
            console.error("Erro no upload das imagens", error);

            const detail =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                "Não foi possível concluir o envio.";

            await Swal.fire({
                icon: "error",
                title: "Erro ao enviar imagens",
                text: detail,
                confirmButtonText: "Ok",
            });
        } finally {
            setIsUploading(false);
            setUploadProgressText("");
        }
    };

    const handleDropZoneClick = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const related = e.relatedTarget;
        if (!related || !e.currentTarget.contains(related)) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) setIsDragging(true);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const files = e.dataTransfer?.files;
        handleAddFiles(files);
    };

    const handleCopyUrl = async (url) => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            Swal.fire({
                icon: "success",
                title: "URL copiada",
                text: "A URL da imagem foi copiada para a área de transferência.",
                timer: 1400,
                showConfirmButton: false,
            });
        } catch (e) {
            Swal.fire({
                icon: "error",
                title: "Não foi possível copiar",
                text: "Seu navegador não permitiu copiar a URL.",
            });
        }
    };

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                width: "100%",
                maxWidth: "100%",
                minHeight: "100vh",
                px: { xs: 2, md: 3 },
                py: 3,
                pt: "80px",
                background: pageBg,
                boxSizing: "border-box",
                overflowX: "hidden",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    mx: 0,
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: "100%",
                        maxWidth: "100%",
                        p: { xs: 2.2, md: 3 },
                        borderRadius: "28px",
                        background: glassBg,
                        backdropFilter: "blur(10px)",
                        border: glassBorder,
                        boxShadow: softShadow,
                        overflow: "hidden",
                        boxSizing: "border-box",
                    }}
                >
                    <Stack
                        spacing={3}
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                width: "100%",
                                maxWidth: "100%",
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", lg: "1.15fr 0.85fr" },
                                gap: 2,
                                alignItems: "stretch",
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: "16px",
                                            display: "grid",
                                            placeItems: "center",
                                            bgcolor:
                                                theme.palette.mode === "dark"
                                                    ? alpha(colors.blueAccent[500], 0.22)
                                                    : alpha(colors.blueAccent[100], 0.9),
                                            color:
                                                theme.palette.mode === "dark"
                                                    ? colors.grey[100]
                                                    : colors.blueAccent[700],
                                            flexShrink: 0,
                                        }}
                                    >
                                        <CollectionsRoundedIcon />
                                    </Box>

                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 24, md: 30 },
                                                fontWeight: 900,
                                                letterSpacing: "-0.03em",
                                                lineHeight: 1.1,
                                            }}
                                        >
                                            Upload de Imagens
                                        </Typography>

                                        <Typography sx={{ color: mutedText, mt: 0.4 }}>
                                            Arraste, visualize, filtre e envie imagens para tratamento no backend e gravação no Firebase.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent={{ xs: "flex-start", lg: "flex-end" }}
                                    flexWrap="wrap"
                                    useFlexGap
                                >
                                    <Chip
                                        icon={<ImageRoundedIcon />}
                                        label={`${selectedItems.length} selecionada(s)`}
                                        sx={{
                                            borderRadius: "999px",
                                            height: 36,
                                            bgcolor: neutralChipBg,
                                        }}
                                    />
                                    <Chip
                                        icon={<Inventory2OutlinedIcon />}
                                        label={formatBytes(totalSize)}
                                        sx={{
                                            borderRadius: "999px",
                                            height: 36,
                                            bgcolor: neutralChipBg,
                                        }}
                                    />
                                    <Chip
                                        icon={<FolderOpenRoundedIcon />}
                                        label={subfolder?.trim() || "productapp"}
                                        sx={{
                                            borderRadius: "999px",
                                            height: 36,
                                            maxWidth: 260,
                                            "& .MuiChip-label": {
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            },
                                            bgcolor: neutralChipBg,
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Box>

                        <Paper
                            elevation={0}
                            sx={{
                                width: "100%",
                                maxWidth: "100%",
                                p: { xs: 1.5, md: 1.8 },
                                borderRadius: "22px",
                                border: glassBorder,
                                bgcolor:
                                    theme.palette.mode === "dark"
                                        ? alpha("#ffffff", 0.03)
                                        : alpha("#ffffff", 0.74),
                                boxSizing: "border-box",
                            }}
                        >
                            <Stack spacing={1.5} sx={{ width: "100%" }}>
                                <Stack
                                    direction={{ xs: "column", xl: "row" }}
                                    spacing={1.5}
                                    alignItems={{ xs: "stretch", xl: "center" }}
                                    sx={{ width: "100%" }}
                                >
                                    <Button
                                        component="label"
                                        variant="contained"
                                        startIcon={<AddPhotoAlternateRoundedIcon />}
                                        disabled={isUploading}
                                        sx={{
                                            minHeight: 48,
                                            borderRadius: "16px",
                                            px: 2.2,
                                            fontWeight: 800,
                                            textTransform: "none",
                                            boxShadow: "none",
                                            flexShrink: 0,
                                        }}
                                    >
                                        Selecionar imagens
                                        <input
                                            ref={inputRef}
                                            hidden
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleFilesSelected}
                                        />
                                    </Button>

                                    <TextField
                                        fullWidth
                                        label="Subpasta no storage"
                                        placeholder="Ex: coleção-outono"
                                        value={subfolder}
                                        disabled={isUploading}
                                        onChange={(e) => setSubfolder(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <FolderOpenRoundedIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            minWidth: 0,
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "16px",
                                                minHeight: 48,
                                            },
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Buscar por nome"
                                        placeholder="Filtrar imagens pelo nome"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchRoundedIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            minWidth: 0,
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "16px",
                                                minHeight: 48,
                                            },
                                        }}
                                    />

                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        startIcon={<ClearAllRoundedIcon />}
                                        disabled={isUploading || !selectedItems.length}
                                        onClick={handleClearAll}
                                        sx={{
                                            minHeight: 48,
                                            borderRadius: "16px",
                                            px: 2,
                                            fontWeight: 700,
                                            textTransform: "none",
                                            flexShrink: 0,
                                        }}
                                    >
                                        Limpar
                                    </Button>

                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={
                                            isUploading ? (
                                                <CircularProgress size={18} color="inherit" />
                                            ) : (
                                                <CloudUploadRoundedIcon />
                                            )
                                        }
                                        disabled={isUploading || !selectedItems.length}
                                        onClick={handleUpload}
                                        sx={{
                                            minHeight: 48,
                                            borderRadius: "16px",
                                            px: 2.6,
                                            fontWeight: 900,
                                            textTransform: "none",
                                            boxShadow: "none",
                                            whiteSpace: "nowrap",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {isUploading ? "Enviando..." : "Enviar imagens"}
                                    </Button>
                                </Stack>

                                <Paper
                                    elevation={0}
                                    onClick={handleDropZoneClick}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    sx={{
                                        width: "100%",
                                        maxWidth: "100%",
                                        p: { xs: 2.2, md: 2.8 },
                                        borderRadius: "22px",
                                        border: isDragging
                                            ? `1px dashed ${theme.palette.primary.main}`
                                            : theme.palette.mode === "dark"
                                                ? "1px dashed rgba(255,255,255,0.14)"
                                                : "1px dashed rgba(15,23,42,0.14)",
                                        bgcolor: isDragging
                                            ? theme.palette.mode === "dark"
                                                ? alpha(theme.palette.primary.main, 0.14)
                                                : alpha(theme.palette.primary.main, 0.08)
                                            : theme.palette.mode === "dark"
                                                ? alpha("#ffffff", 0.02)
                                                : alpha("#0f172a", 0.02),
                                        cursor: "pointer",
                                        transition: "all .2s ease",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        spacing={2}
                                        alignItems={{ xs: "center", md: "center" }}
                                        justifyContent="space-between"
                                        sx={{ width: "100%" }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1.5}
                                            alignItems="center"
                                            sx={{ textAlign: { xs: "center", md: "left" }, minWidth: 0 }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 58,
                                                    height: 58,
                                                    borderRadius: "18px",
                                                    display: "grid",
                                                    placeItems: "center",
                                                    bgcolor:
                                                        theme.palette.mode === "dark"
                                                            ? alpha("#ffffff", 0.05)
                                                            : alpha("#0f172a", 0.05),
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {isDragging ? <FileUploadRoundedIcon /> : <DragIndicatorRoundedIcon />}
                                            </Box>

                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="h6" fontWeight={900}>
                                                    Arraste imagens para cá
                                                </Typography>
                                                <Typography sx={{ color: mutedText }}>
                                                    Ou clique para selecionar vários arquivos do seu computador.
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Chip
                                            label="JPG, PNG, WEBP e similares"
                                            sx={{
                                                borderRadius: "999px",
                                                bgcolor: neutralChipBg,
                                                flexShrink: 0,
                                            }}
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Paper>

                        {!!uploadProgressText && (
                            <Paper
                                elevation={0}
                                sx={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    p: 1.8,
                                    borderRadius: "18px",
                                    border: glassBorder,
                                    bgcolor:
                                        theme.palette.mode === "dark"
                                            ? alpha(colors.blueAccent[700], 0.14)
                                            : alpha(colors.blueAccent[100], 0.5),
                                    boxSizing: "border-box",
                                }}
                            >
                                <Stack spacing={1.2}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={18} />
                                        <Typography fontWeight={800}>{uploadProgressText}</Typography>
                                    </Stack>
                                    <LinearProgress sx={{ borderRadius: 99, height: 8 }} />
                                </Stack>
                            </Paper>
                        )}

                        {!selectedItems.length && (
                            <Paper
                                elevation={0}
                                sx={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    borderRadius: "24px",
                                    border: glassBorder,
                                    bgcolor:
                                        theme.palette.mode === "dark"
                                            ? alpha("#ffffff", 0.025)
                                            : alpha("#ffffff", 0.65),
                                    minHeight: 260,
                                    display: "grid",
                                    placeItems: "center",
                                    textAlign: "center",
                                    p: 3,
                                    boxSizing: "border-box",
                                }}
                            >
                                <Box>
                                    <Box
                                        sx={{
                                            width: 72,
                                            height: 72,
                                            mx: "auto",
                                            mb: 1.5,
                                            borderRadius: "22px",
                                            display: "grid",
                                            placeItems: "center",
                                            bgcolor:
                                                theme.palette.mode === "dark"
                                                    ? alpha("#ffffff", 0.05)
                                                    : alpha("#0f172a", 0.05),
                                        }}
                                    >
                                        <AddPhotoAlternateRoundedIcon sx={{ fontSize: 34, opacity: 0.8 }} />
                                    </Box>

                                    <Typography variant="h5" fontWeight={900}>
                                        Nenhuma imagem selecionada
                                    </Typography>

                                    <Typography sx={{ color: mutedText, mt: 0.8, maxWidth: 520 }}>
                                        Clique em “Selecionar imagens” ou arraste arquivos para a área acima para montar sua grade de envio.
                                    </Typography>
                                </Box>
                            </Paper>
                        )}

                        {!!selectedItems.length && (
                            <Box sx={{ width: "100%", maxWidth: "100%" }}>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", sm: "center" }}
                                    spacing={1}
                                    sx={{ mb: 1.5 }}
                                >
                                    <Box>
                                        <Typography variant="h6" fontWeight={900}>
                                            Pré-visualização
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: mutedText }}>
                                            Revise os arquivos antes de enviar.
                                        </Typography>
                                    </Box>

                                    <Chip
                                        label={`${filteredSelectedItems.length} item(ns)`}
                                        sx={{
                                            borderRadius: "999px",
                                            bgcolor: neutralChipBg,
                                        }}
                                    />
                                </Stack>

                                <Box
                                    sx={{
                                        width: "100%",
                                        maxWidth: "100%",
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
                                        gap: 2,
                                    }}
                                >
                                    {filteredSelectedItems.map((item) => (
                                        <Card
                                            key={item.id}
                                            elevation={0}
                                            className="img-card"
                                            sx={{
                                                borderRadius: "22px",
                                                overflow: "hidden",
                                                border: glassBorder,
                                                bgcolor:
                                                    theme.palette.mode === "dark"
                                                        ? alpha("#ffffff", 0.03)
                                                        : alpha("#ffffff", 0.8),
                                                transition: "transform .2s ease, box-shadow .2s ease",
                                                boxShadow: "none",
                                                minWidth: 0,
                                                "&:hover": {
                                                    transform: "translateY(-4px)",
                                                    boxShadow:
                                                        theme.palette.mode === "dark"
                                                            ? "0 14px 28px rgba(0,0,0,0.24)"
                                                            : "0 16px 28px rgba(15,23,42,0.12)",
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    aspectRatio: "3 / 4",
                                                    overflow: "hidden",
                                                    bgcolor:
                                                        theme.palette.mode === "dark"
                                                            ? alpha("#ffffff", 0.03)
                                                            : "#f3f4f6",
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={item.previewUrl}
                                                    alt={item.name}
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        display: "block",
                                                    }}
                                                />

                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        background:
                                                            "linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0.04))",
                                                        opacity: 0,
                                                        transition: "opacity .2s ease",
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        justifyContent: "space-between",
                                                        p: 1,
                                                        ".img-card:hover &": {
                                                            opacity: 1,
                                                        },
                                                    }}
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={formatBytes(item.size)}
                                                        sx={{
                                                            borderRadius: "999px",
                                                            bgcolor: "rgba(255,255,255,0.88)",
                                                            fontWeight: 700,
                                                        }}
                                                    />

                                                    <Stack direction="row" spacing={0.5}>
                                                        <Tooltip title="Ampliar">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setPreviewImage({ url: item.previewUrl, name: item.name })}
                                                                sx={{
                                                                    bgcolor: "rgba(255,255,255,0.88)",
                                                                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                                                                }}
                                                            >
                                                                <ZoomInRoundedIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Remover">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRemoveItem(item.id)}
                                                                disabled={isUploading}
                                                                sx={{
                                                                    bgcolor: "rgba(255,255,255,0.88)",
                                                                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                                                                }}
                                                            >
                                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Box>
                                            </Box>

                                            <CardContent sx={{ p: 1.3 }}>
                                                <Stack spacing={0.6}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={800}
                                                        sx={{
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden",
                                                            minHeight: 38,
                                                        }}
                                                    >
                                                        {item.name}
                                                    </Typography>

                                                    <Typography variant="caption" sx={{ color: mutedText }}>
                                                        Pronta para envio
                                                    </Typography>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {!!result && (
                            <Paper
                                elevation={0}
                                sx={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    p: { xs: 1.5, md: 2 },
                                    borderRadius: "22px",
                                    border: glassBorder,
                                    bgcolor:
                                        theme.palette.mode === "dark"
                                            ? alpha("#ffffff", 0.03)
                                            : alpha("#ffffff", 0.75),
                                    boxSizing: "border-box",
                                }}
                            >
                                <Stack spacing={2}>
                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        spacing={1.2}
                                        alignItems={{ xs: "stretch", md: "center" }}
                                    >
                                        <Alert
                                            icon={<CheckCircleRoundedIcon fontSize="inherit" />}
                                            severity={Number(result?.error_count || 0) > 0 ? "warning" : "success"}
                                            sx={{ flex: 1, borderRadius: "16px" }}
                                        >
                                            {result?.message || "Processamento concluído."}
                                        </Alert>

                                        <Chip
                                            label={`Enviadas: ${Number(result?.uploaded_count || 0)}`}
                                            color="success"
                                            sx={{ borderRadius: "999px", fontWeight: 800 }}
                                        />
                                        <Chip
                                            label={`Erros: ${Number(result?.error_count || 0)}`}
                                            color={Number(result?.error_count || 0) > 0 ? "warning" : "default"}
                                            sx={{ borderRadius: "999px", fontWeight: 800 }}
                                        />
                                    </Stack>

                                    {!!result?.errors?.length && (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.6,
                                                borderRadius: "18px",
                                                border: glassBorder,
                                                bgcolor:
                                                    theme.palette.mode === "dark"
                                                        ? alpha(colors.redAccent[700], 0.14)
                                                        : alpha(colors.redAccent[100], 0.36),
                                            }}
                                        >
                                            <Stack spacing={1}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <InfoOutlinedIcon color="warning" />
                                                    <Typography fontWeight={900}>Arquivos com erro</Typography>
                                                </Stack>

                                                {result.errors.map((item, idx) => (
                                                    <Typography key={`${item.filename}-${idx}`} variant="body2">
                                                        <b>{item.filename}</b>: {item.error}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </Paper>
                                    )}

                                    {!!result?.uploaded?.length && (
                                        <Box sx={{ width: "100%", maxWidth: "100%" }}>
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                justifyContent="space-between"
                                                alignItems={{ xs: "flex-start", sm: "center" }}
                                                spacing={1}
                                                sx={{ mb: 1.5 }}
                                            >
                                                <Box>
                                                    <Typography variant="h6" fontWeight={900}>
                                                        Imagens enviadas
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: mutedText }}>
                                                        Arquivos já processados e salvos no storage.
                                                    </Typography>
                                                </Box>

                                                <Chip
                                                    label={`${filteredUploadedItems.length} item(ns)`}
                                                    sx={{
                                                        borderRadius: "999px",
                                                        bgcolor: neutralChipBg,
                                                    }}
                                                />
                                            </Stack>

                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    maxWidth: "100%",
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
                                                    gap: 2,
                                                }}
                                            >
                                                {filteredUploadedItems.map((img, index) => (
                                                    <Card
                                                        key={`${img.path}-${index}`}
                                                        elevation={0}
                                                        className="img-card"
                                                        sx={{
                                                            borderRadius: "22px",
                                                            overflow: "hidden",
                                                            border: glassBorder,
                                                            bgcolor:
                                                                theme.palette.mode === "dark"
                                                                    ? alpha("#ffffff", 0.03)
                                                                    : alpha("#ffffff", 0.8),
                                                            transition: "transform .2s ease, box-shadow .2s ease",
                                                            minWidth: 0,
                                                            "&:hover": {
                                                                transform: "translateY(-4px)",
                                                                boxShadow:
                                                                    theme.palette.mode === "dark"
                                                                        ? "0 14px 28px rgba(0,0,0,0.24)"
                                                                        : "0 16px 28px rgba(15,23,42,0.12)",
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                position: "relative",
                                                                aspectRatio: "3 / 4",
                                                                bgcolor:
                                                                    theme.palette.mode === "dark"
                                                                        ? alpha("#ffffff", 0.03)
                                                                        : "#f3f4f6",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src={img.url}
                                                                alt={img.filename}
                                                                sx={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit: "cover",
                                                                    display: "block",
                                                                }}
                                                            />

                                                            <Box
                                                                sx={{
                                                                    position: "absolute",
                                                                    inset: 0,
                                                                    background:
                                                                        "linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0.04))",
                                                                    opacity: 0,
                                                                    transition: "opacity .2s ease",
                                                                    display: "flex",
                                                                    alignItems: "flex-start",
                                                                    justifyContent: "space-between",
                                                                    p: 1,
                                                                    ".img-card:hover &": {
                                                                        opacity: 1,
                                                                    },
                                                                }}
                                                            >
                                                                <Chip
                                                                    size="small"
                                                                    label={`${img.width}x${img.height}`}
                                                                    sx={{
                                                                        borderRadius: "999px",
                                                                        bgcolor: "rgba(255,255,255,0.88)",
                                                                        fontWeight: 700,
                                                                    }}
                                                                />

                                                                <Stack direction="row" spacing={0.5}>
                                                                    <Tooltip title="Ampliar">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => setPreviewImage({ url: img.url, name: img.filename })}
                                                                            sx={{
                                                                                bgcolor: "rgba(255,255,255,0.88)",
                                                                                "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                                                                            }}
                                                                        >
                                                                            <ZoomInRoundedIcon sx={{ fontSize: 18 }} />
                                                                        </IconButton>
                                                                    </Tooltip>

                                                                    <Tooltip title="Abrir em nova aba">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => window.open(img.url, "_blank", "noopener,noreferrer")}
                                                                            sx={{
                                                                                bgcolor: "rgba(255,255,255,0.88)",
                                                                                "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                                                                            }}
                                                                        >
                                                                            <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />
                                                                        </IconButton>
                                                                    </Tooltip>

                                                                    <Tooltip title="Copiar URL">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleCopyUrl(img.url)}
                                                                            sx={{
                                                                                bgcolor: "rgba(255,255,255,0.88)",
                                                                                "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                                                                            }}
                                                                        >
                                                                            <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Box>
                                                        </Box>

                                                        <CardContent sx={{ p: 1.3 }}>
                                                            <Stack spacing={0.8}>
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={800}
                                                                    sx={{
                                                                        display: "-webkit-box",
                                                                        WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: "vertical",
                                                                        overflow: "hidden",
                                                                        minHeight: 38,
                                                                    }}
                                                                >
                                                                    {img.filename}
                                                                </Typography>

                                                                <Chip
                                                                    size="small"
                                                                    label={`${img.width}x${img.height}`}
                                                                    sx={{
                                                                        borderRadius: "999px",
                                                                        width: "fit-content",
                                                                        bgcolor: neutralChipBg,
                                                                    }}
                                                                />

                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: mutedText,
                                                                        wordBreak: "break-all",
                                                                        display: "block",
                                                                    }}
                                                                >
                                                                    {img.path}
                                                                </Typography>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                </Paper>
            </Box>

            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                maxWidth="lg"
                fullWidth
                TransitionComponent={Fade}
                PaperProps={{
                    sx: {
                        borderRadius: "24px",
                        overflow: "hidden",
                        bgcolor: theme.palette.mode === "dark" ? "#111" : "#fff",
                    },
                }}
            >
                <DialogContent sx={{ p: 0, position: "relative", bgcolor: "#000" }}>
                    <IconButton
                        onClick={() => setPreviewImage(null)}
                        sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            zIndex: 2,
                            bgcolor: "rgba(255,255,255,0.88)",
                            "&:hover": {
                                bgcolor: "rgba(255,255,255,1)",
                            },
                        }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>

                    {!!previewImage && (
                        <Box
                            sx={{
                                width: "100%",
                                maxHeight: "85vh",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "#000",
                            }}
                        >
                            <Box
                                component="img"
                                src={previewImage.url}
                                alt={previewImage.name}
                                sx={{
                                    maxWidth: "100%",
                                    maxHeight: "85vh",
                                    objectFit: "contain",
                                    display: "block",
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}